import { getLeaguePlaysBySeason } from '@/utils/api/league';
import { filterByGameLocationsAndResults } from '@/utils/playsFiltering';
import { getGamesBySeason } from '@/utils/api/games';
import getLeaguePlayIndexes from '@/data/leaguePlayIndexes';
import { getTeamsBySeason } from '@/utils/api/teams';

/**
 * This store must have all global league data, like all league games per season,
 * all league plays by season, all league teams per season, all league players by season, etc.
 */
export default {
  namespaced: true,
  state: {
    seasonPlays: [],
    // todo: how to get current season games, because user's login may return another season
    currentSeasonGames: [],
    currentSeasonTeams: [],
    selectedRound: 1,
    booleanAreLiveGames: false,
    booleanIsLiveGamesModalOpen: false,
  },
  getters: {
    seasonPlays: state => state.seasonPlays,
    filteredPlaysStack(state, getters, rootState, rootGetters) {
      return filterByGameLocationsAndResults(
        state.seasonPlays,
        rootGetters['filterGeneral/getPlayingAs'],
        rootGetters['filterGeneral/getResults'],
        rootGetters['filterGeneral/getSelectedGamesIds'],
        getLeaguePlayIndexes(),
        rootGetters['teamsGeneral/team'].team_id,
        true,
      );
    },
    nextGamesSchedule(state) {
      const schedule = state.currentSeasonGames.reduce((accumulator, game) => {
        const gameTmp = { ...game };
        gameTmp.home_team = state.currentSeasonTeams.find(team => team.team_id === game.home_team);
        gameTmp.visiting_team = state.currentSeasonTeams.find(team => team.team_id === game.visiting_team);
        if (!accumulator[game.matchday_abbreviation]) {
          accumulator[game.matchday_abbreviation] = {
            [game.game_status === 3 ? 'canceled' : game.date]: [gameTmp],
          };
        } else if (!accumulator[game.matchday_abbreviation][game.game_status === 3 ? 'canceled' : game.date]) {
          accumulator[game.matchday_abbreviation][game.game_status === 3 ? 'canceled' : game.date] = [gameTmp];
        } else {
          accumulator[game.matchday_abbreviation][game.game_status === 3 ? 'canceled' : game.date].push(gameTmp);
        }
        return accumulator;
      }, {});
      for (let [k, v] of Object.entries(schedule)) {
        schedule[k] = Object.entries(v).map(([k2, v2]) => {
          return {
            date: k2,
            games: v2,
          };
        });
        schedule[k].sort((a, b) => {
          return a.date < b.date ? -1 : a.date > b.date ? 1 : 0;
        });
      }
      return schedule;
    },
    /**
     * @author Rigoberto Sanchez
     * @description get all dates in array format
     * @returns {array} Array of object with matchday (numeric) and date (jsdate)
     */
    matchdaysDates(state, getters) {
      let result = [];
      if (getters.nextGamesSchedule) {
        let matchdays = Object.keys(getters.nextGamesSchedule);
        for (let matchday of matchdays) {
          let matchdayNumber = Number(matchday.replace('M', ''));
          let matchdaysBy = getters.nextGamesSchedule[matchday].map(game => ({
            matchday: matchdayNumber,
            date: new Date(game.date),
          }));
          result = result.concat(matchdaysBy);
        }
      }
      return result;
    },
    /**
     * @author Rigoberto Sanchez
     * @description Obtains the matchday closest to the current date
     * @return {number} returns the number of matchday
     */
    closestMatchDay(state, getters) {
      let today = new Date();
      let currDiff = 0;
      let bestDiff = -new Date(0, 0, 0).valueOf();
      let days = getters.matchdaysDates;
      let bestDate = days.length;
      for (let i = 0; i < days.length; i++) {
        currDiff = Math.abs(days[i].date - today);
        if (currDiff < bestDiff) {
          bestDate = i;
          bestDiff = currDiff;
        }
      }
      return days[bestDate] ? days[bestDate].matchday : 1;
    },
  },
  mutations: {
    setBooleanIsLiveGamesModalOpen(state, booleanIsLiveGamesModalOpen) {
      state.booleanIsLiveGamesModalOpen = booleanIsLiveGamesModalOpen;
    },
    setBooleanAreLiveGames(state, booleanAreLiveGames) {
      state.booleanAreLiveGames = booleanAreLiveGames;
    },
    updateSeasonPlays(state, seasonPlays) {
      state.seasonPlays = seasonPlays;
    },
    updateCurrentSeasonTeams(state, currentSeasonTeams) {
      state.currentSeasonTeams = currentSeasonTeams;
    },
    updateCurrentSeasonGames(state, currentSeasonGames) {
      state.currentSeasonGames = currentSeasonGames;
    },
    updateSelectedRound(state, selectedRound) {
      state.selectedRound = selectedRound;
    },
  },
  actions: {
    async getSeasonPlays({ commit, rootGetters }, returnData = false) {
      try {
        const seasonPlays = await getLeaguePlaysBySeason({ seasonId: rootGetters['filterGeneral/getSeasonId'] });
        if (returnData) {
          return seasonPlays;
        }
        commit('updateSeasonPlays', seasonPlays);
      } catch (e) {
        return Promise.reject(e);
      }
    },
    async getCurrentSeasonGames({ commit, rootGetters }) {
      try {
        const currentSeasonGames = await getGamesBySeason({ seasonId: rootGetters['loginGeneral/seasonId'] });
        commit('updateCurrentSeasonGames', currentSeasonGames);
      } catch (e) {
        return Promise.reject(e);
      }
    },
    async getCurrentSeasonTeams({ commit, rootGetters }) {
      try {
        const response = await getTeamsBySeason({ seasonId: rootGetters['loginGeneral/seasonId'] });
        commit('updateCurrentSeasonTeams', response.teams);
      } catch (e) {
        return Promise.reject(e);
      }
    },
    async isDataFilled({ state, dispatch, commit }) {
      try {
        if (state.currentSeasonGames.length > 0 && state.currentSeasonTeams.length > 0) {
          commit('setIsSectionLoading', false, { root: true });
          return true;
        }
        const promises = [undefined, undefined];
        if (state.currentSeasonGames.length === 0) {
          promises[0] = dispatch('getCurrentSeasonGames');
        }
        if (state.currentSeasonTeams.length === 0) {
          promises[1] = dispatch('getCurrentSeasonTeams');
        }
        await Promise.all(promises);
        return false;
      } catch (e) {
        throw new Error(e);
      }
    },
  },
};
